import {
	EuiButton,
	EuiForm,
	EuiSpacer,
} from '@equipmentshare/ds2'
import { AddLineIcon } from '@equipmentshare/ds2-icons'
import PartRow from '../PartRow'
import { StorePart, Part } from 'graphql'
import { SetUnexpectedCost } from '../../helpers/validateForm'

/**
 * Minimal part information needed for display purposes
 * This allows us to display part info even when the full Part object isn't available
 * Compatible with GraphQL query results that may include __typename fields
 */
export type PartDisplayInfo = Pick<Part, 'id' | 'partNumber' > & {
  provider: Pick<Part['provider'], 'id' | 'name'>,
} & {
  partClass: Pick<Part['partClass'], 'description'>,
}

export type TransactionItemFormState = {
	idx: number,
	part?: PartDisplayInfo,
	storePart?: StorePart,
	selectedProductId?: number,
	isUnavailable?: boolean,
	quantity: string,
	costPerItem: string,
	validation: {
		quantity: {
			isInvalid: boolean,
			error?: string,
		},
		costPerItem: {
			isInvalid: boolean,
			error?: string,
		},
		part: {
			isInvalid: boolean,
			error?: string,
		},
	},
}

export type AddPartsFormProps = {
	isDisabled?: boolean,
	setItems: ( items: TransactionItemFormState[] | ( ( prev: TransactionItemFormState[] ) => TransactionItemFormState[] ) ) => void,
	items: TransactionItemFormState[],
	storeId?: number,
	validation?: {
		isInvalid: boolean,
		error?: string,
	},
	setUnexpectedCost: SetUnexpectedCost,
	toId?: number,
}

export function getEmptyItem( index: number ): TransactionItemFormState {
	return {
		idx: index,
		part: undefined,
		storePart: undefined,
		quantity: '',
		costPerItem: '',
		validation: {
			quantity: { isInvalid: false },
			costPerItem: { isInvalid: false },
			part: { isInvalid: false },
		}
	}
}

export default function AddPartsForm( { items, setItems, storeId, isDisabled, validation, setUnexpectedCost, toId }: AddPartsFormProps ) {
	const setItem = ( idx: number ) => ( item: TransactionItemFormState ) => (
		setItems( ( prev ) => prev.map( ( mapItem ) => ( mapItem.idx === idx ? item : mapItem ) ) )
	)

	const addItem = () => (
		setItems( ( prev ) => {
			const nextIdx = prev.reduce( ( max, item ) => Math.max( max, item.idx ), -1 ) + 1
			return [ ...prev, getEmptyItem( nextIdx ) ]
		} )
	)

	const removeItem = ( idx: number ) => () => (
		setItems( ( prev ) => prev.filter( ( item ) => item.idx !== idx ) )
	)

	const markUnavailable = ( idx: number, _productId: number ) => {
		setItems( ( prev ) => prev.map( ( item ) => {
			if ( item.idx === idx ) {
				return {
					...item,
					isUnavailable: true,
				}
			}
			return item
		} ) )
	}

	return (
		<div>
			<EuiForm {...validation}>
				{ items.map( ( item ) => (
					<PartRow
						key={`partRow${item.idx}`}
						isDisabled={isDisabled}
						item={item}
						setItem={setItem( item.idx )}
						removeItem={removeItem( item.idx )}
						storeId={storeId}
						setUnexpectedCost={setUnexpectedCost}
						toId={toId}
						onMarkUnavailable={markUnavailable}
					/>
				) ) }
			</EuiForm>
			<EuiSpacer size="s" />
			<EuiButton
				id="addRowBtn"
				color="text"
				iconType={AddLineIcon}
				iconSide="left"
				isDisabled={isDisabled}
				onClick={addItem}
			>
				Add another item
			</EuiButton>
		</div>
	)
}
